import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import type { MentionPerson } from './useMentionAutocomplete';
import type { CSSProperties, RefObject } from 'react';

interface Props {
  open: boolean;
  results: MentionPerson[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onSelect: (person: MentionPerson) => void;
  menuRef: RefObject<HTMLDivElement>;
  menuStyle: CSSProperties | null;
  /** Collision-free roster colors by person name, from the owning modal. */
  colors?: Map<string, string>;
}

/**
 * The `@`-mention teammate list, portaled and anchored to the textarea by
 * `useMentionAutocomplete`. Keyboard nav lives in the hook; this renders the
 * rows and mirrors the highlighted row on hover. `onMouseDown` (not click)
 * selects, so the pick fires before the textarea loses focus/blur.
 */
export const MentionSuggestions: React.FC<Props> = ({
  open,
  results,
  activeIndex,
  setActiveIndex,
  onSelect,
  menuRef,
  menuStyle,
  colors,
}) => {
  if (!open || !menuStyle) return null;

  return createPortal(
    <Menu
      ref={menuRef}
      style={menuStyle}
      role="listbox"
      data-testid="comment-mention-suggestions"
    >
      {results.map((person, i) => (
        <Row
          key={person.id}
          type="button"
          role="option"
          aria-selected={i === activeIndex}
          $active={i === activeIndex}
          data-testid="comment-mention-option"
          onMouseEnter={() => setActiveIndex(i)}
          onMouseDown={e => {
            // Select before the textarea blurs.
            e.preventDefault();
            onSelect(person);
          }}
        >
          <SpeakerAvatar
            name={person.name}
            // Collision-free roster color, keyed by display name so the same
            // person matches their comment-card avatar (authorId is a Clerk
            // id, unrelated to the roster person id — name is the shared
            // identity across surfaces).
            color={
              colors?.get(person.name) ?? getSpeakerColorFromLabel(person.name)
            }
            size={20}
            initialsColor="var(--gray-900)"
          />
          <Name>{person.name}</Name>
          {person.email && <Email>{person.email}</Email>}
        </Row>
      ))}
    </Menu>,
    document.body,
  );
};

export default MentionSuggestions;

// Rounded-row menu (docs/design-system.md "Dropdown / Popover Menus").
// Positioning comes from useAnchoredDropdown's inline style; only visuals here.
const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--elevation-toast);
`;

const Row = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: ${props =>
    props.$active ? 'var(--surface-elevated)' : 'transparent'};
  text-align: left;
  cursor: pointer;
`;

const Name = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  font-size: var(--text-xs, 12px);
  color: var(--foreground);
`;

// Secondary email, muted and truncated, filling the remaining row width after
// the name (shown only when the teammate has an email).
const Email = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-xs, 12px);
  color: var(--text-muted);
`;
